import React, { FC, useEffect, useState } from 'react';
import { Button, Col, Form, Input, Modal, Row, Select, Tabs } from 'antd';
import { DeleteOutlined } from '@ant-design/icons';

import EnvironmentSelect from '@lib/components/env-select';

import { MessageDto } from '@services/_api';
import { getUpdateMessages, updateMessage } from '@services/game-config/update-message';

const UpdateMessageScreen: FC = () => {
  const [environment, setEnvironment] = useState<string>('');
  const [messages, setMessages] = useState<MessageDto[]>([]);
  const [addLanguageValue, setAddLanguageValue] = useState<string>('');
  const [activeTab, setActiveTab] = useState<string>('');
  const [type, setType] = useState<string>('data');

  const fetchMessages = async () => {
    getUpdateMessages(environment, type)
      .then((res) => {
        if (res.ok && res.data) {
          setMessages(res.data);
          if (res.data.length > 0) {
            setActiveTab(res.data[0].language);
          }
        }
      })
      .catch((err) => {
        setMessages([]);
        setActiveTab('');
        console.error(err);
      });
  };
  useEffect(() => {
    if (environment && type) {
      fetchMessages();
    }
  }, [environment, type]);

  const [form] = Form.useForm();

  const handleAddLanguage = () => {
    if (addLanguageValue && !messages.some((message) => message.language === addLanguageValue)) {
      const newMessage: MessageDto = {
        language: addLanguageValue,
        // eslint-disable-next-line @typescript-eslint/camelcase
        update_title: '',
        message: '',
        upgrade: '',
        support: '',
        userid: '',
        // eslint-disable-next-line @typescript-eslint/camelcase
        ios_url: '',
        // eslint-disable-next-line @typescript-eslint/camelcase
        android_url: '',
        // eslint-disable-next-line @typescript-eslint/camelcase
        steam_url: '',
        // eslint-disable-next-line @typescript-eslint/camelcase
        epic_url: '',
      };
      setMessages([...messages, newMessage]);
      setActiveTab(addLanguageValue);
      setAddLanguageValue('');
    }
  };

  const handleDeleteLanguage = (index: number) => {
    setMessages(messages.filter((_, i) => i !== index));
  };

  const handleUpdate = (index: number, field: string, value: string) => {
    const newMessages = [...messages];
    newMessages[index] = { ...newMessages[index], [field]: value };
    setMessages(newMessages);
  };

  const handleSave = () => {
    if (!environment) {
      Modal.error({
        title: 'Environment is required',
        content: 'Environment is required',
      });
      return;
    }
    updateMessage(environment, type, messages).then((res) => {
      if (res.ok) {
        Modal.info({
          title: 'Update message success',
          content: 'Update message success',
          onOk: () => {
            fetchMessages();
          },
        });
      } else {
        Modal.error({
          title: 'Update message failed',
          content: 'Update message failed',
        });
      }
    });
  };

  return (
    <div className="flex flex-col flex-1 max-h-full min-h-full p-4">
      <Form form={form} layout="horizontal">
        <Form.Item label="Environment" name="environment" rules={[{ required: true }]}>
          <EnvironmentSelect style={{ width: 200 }} onChange={setEnvironment} />
        </Form.Item>

        <Form.Item label="Type" name="type" rules={[{ required: true }]}>
          <Select
            placeholder="Select type"
            style={{ width: 200 }}
            options={[
              { label: 'Data', value: 'data' },
              { label: 'Client', value: 'client' },
            ]}
            value={type}
            onChange={setType}
            defaultValue="data"
          />
        </Form.Item>
        {environment && (
          <>
            <div className="flex gap-4">
              <Input
                placeholder="Language"
                value={addLanguageValue}
                onChange={(e) => setAddLanguageValue(e.target.value)}
                style={{ width: 200 }}
              />
              <Button type="primary" onClick={handleAddLanguage}>
                Add Language
              </Button>
              <Button type="primary" onClick={handleSave}>
                Save
              </Button>
            </div>

            <Tabs activeKey={activeTab} onChange={(key) => setActiveTab(key)}>
              {messages.map((message, index) => (
                <Tabs.TabPane
                  key={message.language}
                  tab={(
                    <div className="flex items-center gap-2">
                      {message.language}
                      <Button
                        shape="circle"
                        size="small"
                        type="text"
                        danger
                        onClick={() => handleDeleteLanguage(index)}
                      >
                        <DeleteOutlined style={{ marginRight: 0 }} />
                      </Button>
                    </div>
                  )}
                  className="flex flex-col gap-4"
                >
                  {[
                    {
                      label: 'Update Title',
                      value: message.update_title,
                      onChange: (e: React.ChangeEvent<HTMLInputElement>) =>
                        handleUpdate(index, 'update_title', e.target.value),
                    },
                    {
                      label: 'Message',
                      value: message.message,
                      onChange: (e: React.ChangeEvent<HTMLInputElement>) =>
                        handleUpdate(index, 'message', e.target.value),
                    },
                    {
                      label: 'Upgrade',
                      value: message.upgrade,
                      onChange: (e: React.ChangeEvent<HTMLInputElement>) =>
                        handleUpdate(index, 'upgrade', e.target.value),
                    },
                    {
                      label: 'Support',
                      value: message.support,
                      onChange: (e: React.ChangeEvent<HTMLInputElement>) =>
                        handleUpdate(index, 'support', e.target.value),
                    },
                    {
                      label: 'User ID',
                      value: message.userid,
                      onChange: (e: React.ChangeEvent<HTMLInputElement>) =>
                        handleUpdate(index, 'userid', e.target.value),
                    },
                    {
                      label: 'iOS URL',
                      value: message.ios_url,
                      onChange: (e: React.ChangeEvent<HTMLInputElement>) =>
                        handleUpdate(index, 'ios_url', e.target.value),
                    },
                    {
                      label: 'Android URL',
                      value: message.android_url,
                      onChange: (e: React.ChangeEvent<HTMLInputElement>) =>
                        handleUpdate(index, 'android_url', e.target.value),
                    },
                    {
                      label: 'Steam URL',
                      value: message.steam_url,
                      onChange: (e: React.ChangeEvent<HTMLInputElement>) =>
                        handleUpdate(index, 'steam_url', e.target.value),
                    },
                    {
                      label: 'Epic URL',
                      value: message.epic_url,
                      onChange: (e: React.ChangeEvent<HTMLInputElement>) =>
                        handleUpdate(index, 'epic_url', e.target.value),
                    },
                  ].map((item) => (
                    <Row key={item.label} gutter={8}>
                      <Col span={6}>{item.label}</Col>
                      <Col span={18}>
                        <Input
                          placeholder={item.label}
                          value={item.value}
                          onChange={item.onChange}
                        />
                      </Col>
                    </Row>
                  ))}
                </Tabs.TabPane>
              ))}
            </Tabs>
          </>
        )}
      </Form>
    </div>
  );
};

export default UpdateMessageScreen;
